import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import useZibi from "../../zibi/UseZibi";
import { colors } from "../../maps/colors";
import { Tooltip } from "@mui/material";
import SensorStatus from "../SensorStatus";
import Icn from "../Icn";
import { api } from "../../maps/data.js";

const WrapperStyled = styled.div`
  position: relative;
`;

const BoxWrapperStyled = styled.div`
  position: relative;
`;

const ShelfBoxStyled = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const ShelfBoxDotStyled = styled.div`
  position: absolute;
  width: 4px;
  height: 4px;
  background: #fff;
  top: -2px;
  left: -2px;
`;

const ShelfAlikeStyled = styled.div`
  position: absolute;
  min-height: 1px;
  padding-right: 15px;
  padding-left: 15px;
  width: 100%;
  border-bottom: 20px solid #a5a5a5;
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  bottom: 0;

  &:after {
    content: "";
    background: #686868;
    height: 10px;
    width: calc(100% + 20px);
    position: absolute;
    top: 20px;
    left: 0;
    right: 0;
    z-index: 1;
    margin: 0 -10px;
  }
`;

const ShelfShapesBoxStyled = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(rgb(100, 100, 100), rgb(10, 10, 10));
  border-left: 1px solid #a5a5a5;
`;

const BoxItemStyled = styled.div`
  position: absolute;
  height: fit-content;
  width: fit-content;

  img {
    height: 100%;
    width: 100%;
  }
`;

const BoardInfoStyled = styled.div`
  position: absolute;
  color: #fff;
  bottom: 1px;
  right: 4px;
  font-size: 10px;
  font-weight: bold;
`;

const WeightDataStyled = styled.div`
  position: absolute;
  z-index: 1;
  right: 12px;
  top: -3px;
  font-size: 12px;
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.3s;

  &.dis {
    visibility: visible;
    opacity: 1;
    transition: opacity 0.3s;
  }
`;

const ShelveItem = ({
  fieldSize,
  shelf,
  products,
  style,
  showProductImages,
  shelfShapes,
  isPreviewForCamera,
  rotate,
  indications,
  field,
  cameraId,
  weight,
  openWeightHandle,
  forceUpdateFields,
}) => {
  const rightBoxRef = useRef(null);

  const [showWeight, s_showWeight] = useState(false);
  const [previousShelfShapes, setPreviousShelfShapes] = useState([]);

  const [productsByBarCode] = useZibi("productsByBarCode");
  const [sensorStatuses] = useZibi("sensorStatuses");

  const name = shelf?.sensor?.boardId;
  const sensorId = shelf?.sensorId;

  const shelfHeight = shelf?.sensor?.height;
  const shelfWidth = shelf?.sensor?.width;
  const sensorStatusData = sensorStatuses[sensorId];
  const status = sensorStatusData?.status;
  const statusUpdateAt = sensorStatusData?.statusUpdateAt;
  const floorWidth = field?.edge?.floor?.width;
  const floorHeight = field?.edge?.floor?.height;
  const fieldHeight = field?.height;
  const fieldXFactor = field?.edge?.floor?.floorEdgeFields?.find(
    (f) => f.fieldId === field._id
  )?.xFactor;
  const fieldYFactor = field?.edge?.floor?.floorEdgeFields?.find(
    (f) => f.fieldId === field._id
  )?.yFactor;
  const calibration = shelf?.sensor?.calibration || [];

  useEffect(() => {
    s_showWeight(true);

    setTimeout(() => {
      s_showWeight(false);
    }, 3000);
  }, [weight]);

  useEffect(() => {
    if (shelfShapes && shelfShapes.length > 0) {
      setPreviousShelfShapes(shelfShapes);
    }
  }, [shelfShapes]);

  const boxStyle = {
    height: "150px",
    marginBottom: "0",
  };
  if (fieldSize) {
    const boxHeight = parseInt(fieldSize / 6 + 20);
    boxStyle.height = `${boxHeight}px`;
  }
  if (isPreviewForCamera) {
    boxStyle.height = "100%";
    boxStyle.marginBottom = "0";
  }

  const calibrate = async () => {
    await api({
      type: "calibrateShelf",
      data: { sensorId, cameraId },
    });
    forceUpdateFields();
  };

  const removeCalibration = async () => {
    await api({
      type: "calibrateShelf",
      data: {
        sensorId,
        cameraId,
        clear: true,
      },
    });
  };

  return (
    <WrapperStyled data="shelve-item" style={style}>
      <WeightDataStyled
        className={showWeight ? "dis" : ""}
        style={{ color: weight > 0 ? "green" : "red" }}
      >
        {weight}
      </WeightDataStyled>
      <BoxWrapperStyled style={boxStyle}>
        <ShelfBoxStyled
          data="shelve-box"
          style={{
            background: isPreviewForCamera
              ? "transparent"
              : "linear-gradient(rgb(100, 100, 100), rgb(10, 10, 10))",
          }}
        >
          {!isPreviewForCamera && <ShelfAlikeStyled />}

          {products?.map((prod, iddx) => {
            const barcode = prod.label.split("_")[0];
            const product = productsByBarCode[barcode.toString()];
            const boundingBoxPr = prod.boundingBoxPr;
            let backGroundColor = colors.WHITE;
            if (product) {
              backGroundColor = colors.RED1;
            }

            let left = `${boundingBoxPr.xMin}%`;
            let top = `${boundingBoxPr.yMin}%`;
            let width = `${boundingBoxPr.xMax - boundingBoxPr.xMin}%`;
            let height = `${boundingBoxPr.yMax - boundingBoxPr.yMin}%`;

            if (rotate) {
              if (rotate === 90) {
                width = `${boundingBoxPr.yMax - boundingBoxPr.yMin}%`;
                height = `${boundingBoxPr.xMax - boundingBoxPr.xMin}%`;
                left = `${100 - boundingBoxPr.yMin}%`;
                top = `${boundingBoxPr.xMin}%`;
              }
              if (rotate === 180) {
                width = `${boundingBoxPr.yMax - boundingBoxPr.yMin}%`;
                height = `${boundingBoxPr.xMax - boundingBoxPr.xMin}%`;
                left = `${100 - boundingBoxPr.yMin}%`;
                top = `${boundingBoxPr.xMin}%`;
              }
              if (rotate === 270) {
                width = `${boundingBoxPr.yMax - boundingBoxPr.yMin}%`;
                height = `${boundingBoxPr.xMax - boundingBoxPr.xMin}%`;
                left = `${100 - boundingBoxPr.yMin}%`;
                top = `${boundingBoxPr.xMin}%`;
              }
            }

            return (
              <Tooltip
                key={iddx + product?.name}
                title={
                  <div>
                    <div>{product ? product?.name : ""}</div>
                    <div>Score: {prod.score}</div>
                  </div>
                }
              >
                <BoxItemStyled
                  className="shelf-box-s"
                  style={{
                    left: left,
                    top: top,
                    width: width,
                    height: height,
                    border: isPreviewForCamera
                      ? `1px solid ${backGroundColor}`
                      : "none",
                  }}
                >
                  {showProductImages && product && (
                    <img src={product.imageUrl} alt="" />
                  )}
                </BoxItemStyled>
              </Tooltip>
            );
          })}
        </ShelfBoxStyled>
      </BoxWrapperStyled>
      {!isPreviewForCamera && (
        <BoxWrapperStyled
          data="shelve-poiue"
          ref={rightBoxRef}
          style={boxStyle}
        >
          <ShelfShapesBoxStyled>
            <SensorStatus status={status} statusUpdateAt={statusUpdateAt} />
            <BoardInfoStyled>{name}</BoardInfoStyled>

            {((shelfShapes && shelfShapes.length > 0) ? shelfShapes : previousShelfShapes)?.map((shlfItem, idx) => {
              // const label = products.find(p=> p?.productId === shlfItem?.productId)?.label;
              let product;
              if (shlfItem.productId) {
                product = Object.values(productsByBarCode).find(
                  (p) => p._id === shlfItem.productId
                );
              }

              const boundingBoxPr = shlfItem.boundingBoxPr;
              const label = shlfItem.label;

              let left = `${boundingBoxPr.xMin}%`;
              let top = `${boundingBoxPr.yMin}%`;
              let width = `${boundingBoxPr.xMax - boundingBoxPr.xMin}%`;
              let height = `${boundingBoxPr.yMax - boundingBoxPr.yMin}%`;

              let borderStyle = "solid";
              let borderRadius = "50%";

              switch (label) {
                case "six_pack":
                  borderRadius = "20%";
                  break;
                case "dash_circle":
                  borderStyle = "dotted";
                  break;
                case "circle":
                  break;
                case "cereal":
                  borderRadius = 0;
                  break;
                default:
                  borderRadius = 0;
                  break;
              }

              let borderColor = colors.RED1;
              if (product) {
                borderColor = colors.SUCCESS_GREEN;
              } else if (shlfItem.isVisible) {
                borderColor = colors.ORANGE;
              }

              return (
                <BoxItemStyled
                  key={idx + "ddaw" + shlfItem.productId}
                  title={
                    shlfItem.isCameraVerified && product ? product?.name : ""
                  }
                  style={{
                    left: left,
                    top: top,
                    width: width,
                    height: height,
                    border: `1px ${borderStyle} ${borderColor}`,
                    borderRadius: borderRadius,
                  }}
                >
                  <div style={{ fontSize: 4, color: "white" }}>
                    {shlfItem.isCameraVerified && product
                      ? product?.name
                      : ""}
                  </div>
                </BoxItemStyled>
              );
            })}

            {floorWidth &&
              floorHeight &&
              indications
                ?.filter((poi) => poi?.data?.cameraId === cameraId)
                ?.map((ind, idx) => {
                  const unit = ind.data.unit || "pixel";

                  let percentYPosition = ind.data.y;
                  let percentXPosition = ind.data.x;

                  if (unit === "pixel") {
                    const dotPositionInBoxX = ind.data.x - fieldXFactor;
                    const dotPositionInBoxY =
                      ind.data.y - fieldYFactor - (fieldHeight - shelfHeight);

                    percentYPosition = (dotPositionInBoxY * 100) / shelfHeight;
                    percentXPosition = (dotPositionInBoxX * 100) / shelfWidth;
                  }

                  const dotStyle = {
                    top: `${percentYPosition}%`,
                    left: `${percentXPosition}%`,
                    background: ind.data.color || "#000",
                  };

                  return (
                    <ShelfBoxDotStyled key={"dotkey" + idx} style={dotStyle} />
                  );
                })}

            <div style={{ position: "absolute", bottom: -5, zIndex: 999 }}>
              <Icn
                onClick={() => calibrate()}
                style={{ fontSize: 14, cursor: "pointer" }}
              >
                calibrationIcon
              </Icn>
              <Icn
                onClick={() => removeCalibration()}
                style={{ fontSize: 14, cursor: "pointer" }}
              >
                delete
              </Icn>
              {shelf?.sensor?.type === 3 && (
                <Icn
                  onClick={(e) =>
                    openWeightHandle && openWeightHandle(e, shelf?.sensor?._id)
                  }
                  style={{ fontSize: 14, cursor: "pointer" }}
                >
                  scaleIcon
                </Icn>
              )}
            </div>

            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
              }}
            >
              {calibration
                .filter((cal) => cal.cameraId === cameraId)
                .map((c, idxx) => (
                  <React.Fragment key={idxx + "sdd3"}>
                    <div
                      style={{
                        position: "absolute",
                        width: 5,
                        height: 1,
                        background: "blue",
                        left: `calc(${c.camera.x}% - 2px)`,
                        top: `${c.camera.y}%`,
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        width: 1,
                        height: 5,
                        background: "blue",
                        left: `${c.camera.x}%`,
                        top: `calc(${c.camera.y}% - 2px)`,
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        width: 5,
                        height: 1,
                        background: "green",
                        left: `calc(${c.shelf.x}% - 2px)`,
                        top: `${c.shelf.y}%`,
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        width: 1,
                        height: 5,
                        background: "green",
                        left: `${c.shelf.x}%`,
                        top: `calc(${c.shelf.y}% - 2px)`,
                      }}
                    />
                  </React.Fragment>
                ))}
            </div>
          </ShelfShapesBoxStyled>
        </BoxWrapperStyled>
      )}
    </WrapperStyled>
  );
};

export default ShelveItem;
